import { ref, computed } from 'vue'
import ApiService from '../services/api.js'
import { collectAllHighlights } from '../utils/highlightsPaging.js'

// ─── Module-level state ─────────────────────────────────────────────────────
const all = ref([])           // current page of highlights
const total = ref(0)
const offset = ref(0)
const limit = ref(50)
const isLoading = ref(false)
const lastFetchedAt = ref(0)
let inFlight = null

// Per-link counts: { [linkId]: count }. Populated lazily by fetchCounts() and
// kept in sync by save()/remove() so the bookmarks grid chip stays accurate
// without round-trips.
const counts = ref({})
const countsLoadedAt = ref(0)
let countsInFlight = null

/** Unwraps a `{highlights: [...]}` payload into mapped, non-null rows. */
function mapHighlightsResponse(resp) {
  const items = Array.isArray(resp?.highlights) ? resp.highlights : []
  return items.map(mapHighlight).filter(Boolean)
}

function mapHighlight(h) {
  if (!h) return null
  return {
    id: h.id,
    linkId: h.link_id,
    text: h.text || '',
    note: h.note || '',
    color: h.color || '#FBBF24',
    anchorTextBefore: h.anchor_text_before || '',
    anchorTextAfter: h.anchor_text_after || '',
    anchorCssSelector: h.anchor_css_selector || '',
    anchorTextOffset: h.anchor_text_offset ?? null,
    pageScrollPct: h.page_scroll_pct ?? null,
    createdAt: h.created_at,
    updatedAt: h.updated_at,
    linkUrl: h.link_url || '',
    linkTitle: h.link_title || '',
    linkImage: h.link_image || ''
  }
}

export function useHighlightsStore() {
  async function fetchPage(token, { offset: ofs = 0, limit: lim = 50, force = false } = {}) {
    if (inFlight && !force) return inFlight
    isLoading.value = true
    inFlight = (async () => {
      try {
        const resp = await ApiService.getHighlights({ offset: ofs, limit: lim }, token)
        all.value = mapHighlightsResponse(resp)
        total.value = resp?.total ?? all.value.length
        offset.value = resp?.offset ?? ofs
        limit.value = resp?.limit ?? lim
        lastFetchedAt.value = Date.now()
        return all.value
      } finally {
        isLoading.value = false
        inFlight = null
      }
    })()
    return inFlight
  }

  async function fetchForUrl(url, token) {
    const resp = await ApiService.getHighlightsForUrl(url, token)
    return mapHighlightsResponse(resp)
  }

  async function fetchForLink(linkId, token) {
    const resp = await ApiService.getHighlightsForLink(linkId, token)
    return mapHighlightsResponse(resp)
  }

  async function search(query, params = {}, token) {
    const resp = await ApiService.searchHighlights(query, params, token)
    const items = mapHighlightsResponse(resp)
    return {
      items,
      total: resp?.total ?? items.length,
      offset: resp?.offset ?? 0,
      limit: resp?.limit ?? items.length
    }
  }

  /**
   * Loads EVERY highlight (optionally the ones matching `query`) for the
   * Markdown export, paging until the server's `total` is reached.
   *
   * Deliberately does NOT write the module-level `all`/`total`/`offset` refs:
   * an export must not clobber the page the list view is currently showing.
   */
  async function fetchAll(token, { query = '', pageSize, maxItems } = {}) {
    const q = String(query || '').trim()
    const fetchPage = async ({ offset: ofs, limit: lim }) => {
      const resp = q
        ? await ApiService.searchHighlights(q, { offset: ofs, limit: lim }, token)
        : await ApiService.getHighlights({ offset: ofs, limit: lim }, token)
      return { items: mapHighlightsResponse(resp), total: resp?.total }
    }
    return collectAllHighlights(fetchPage, { pageSize, maxItems })
  }

  async function save(highlight, token) {
    const payload = {
      id: highlight.id,
      link_id: highlight.linkId,
      url: highlight.url, // optional, used when no linkId yet
      page_title: highlight.pageTitle,
      text: highlight.text,
      note: highlight.note,
      color: highlight.color,
      anchor_text_before: highlight.anchorTextBefore,
      anchor_text_after: highlight.anchorTextAfter,
      anchor_css_selector: highlight.anchorCssSelector,
      anchor_text_offset: highlight.anchorTextOffset,
      page_scroll_pct: highlight.pageScrollPct
    }
    const resp = await ApiService.saveHighlight(payload, token)
    const saved = mapHighlight(resp?.highlight)
    if (saved) {
      const idx = all.value.findIndex(h => h.id === saved.id)
      if (idx >= 0) all.value.splice(idx, 1, saved)
      else all.value.unshift(saved)
      // Bump count only on new highlights (not edits of existing ones)
      if (idx < 0 && saved.linkId) {
        counts.value = { ...counts.value, [saved.linkId]: (counts.value[saved.linkId] || 0) + 1 }
      }
    }
    return saved
  }

  async function remove(highlightId, token) {
    const removed = all.value.find(h => h.id === highlightId)
    await ApiService.deleteHighlight(highlightId, token)
    all.value = all.value.filter(h => h.id !== highlightId)
    if (total.value > 0) total.value -= 1
    if (removed?.linkId) {
      const next = (counts.value[removed.linkId] || 0) - 1
      const updated = { ...counts.value }
      if (next > 0) updated[removed.linkId] = next
      else delete updated[removed.linkId]
      counts.value = updated
    }
  }

  async function fetchCounts(token, { force = false } = {}) {
    if (countsInFlight) return countsInFlight
    if (!force && countsLoadedAt.value && Date.now() - countsLoadedAt.value < 60_000) {
      return counts.value
    }
    countsInFlight = (async () => {
      try {
        const resp = await ApiService.getHighlightCounts(token)
        const raw = resp?.counts || {}
        // Backend serialises long keys as strings — normalise to numbers
        const normalised = {}
        for (const [k, v] of Object.entries(raw)) {
          const id = Number(k)
          if (Number.isFinite(id)) normalised[id] = Number(v) || 0
        }
        counts.value = normalised
        countsLoadedAt.value = Date.now()
        return counts.value
      } finally {
        countsInFlight = null
      }
    })()
    return countsInFlight
  }

  function getCount(linkId) {
    if (!linkId) return 0
    return counts.value[linkId] || counts.value[Number(linkId)] || 0
  }

  return {
    all,
    total,
    offset,
    limit,
    isLoading,
    lastFetchedAt,
    hasMore: computed(() => all.value.length < total.value),
    fetchPage,
    fetchForUrl,
    fetchForLink,
    search,
    fetchAll,
    save,
    remove,
    counts,
    countsLoadedAt,
    fetchCounts,
    getCount
  }
}

export { mapHighlight }
